/* Session 343e — XR debug log clipboard helper.
 *
 * Sister stream 343c mirrors every `[XR]` console line into
 * localStorage under XR_DEBUG_LOG_KEY. This module is the readback
 * half: read, format with a header, and (when invoked from the UI)
 * push to clipboard.
 *
 * Kept tiny + side-effect-free so the formatter is unit-testable
 * without mocking `navigator.clipboard`. */
export const XR_DEBUG_LOG_KEY = "knaad-xr-debug-log";
const EMPTY_MESSAGE = "(no XR log entries yet — run a VR session first to capture data)";
/** Coerce one stored entry to a single-line string. Tolerates both raw
 *  strings and `{ ts, msg }`-shaped objects so the format-on-write
 *  contract on the producer side can evolve without breaking us. */
export function formatXRLogEntry(entry) {
    if (typeof entry === "string")
        return entry;
    if (entry && typeof entry === "object") {
        const obj = entry;
        const ts = typeof obj.ts === "string" || typeof obj.ts === "number"
            ? String(obj.ts) : null;
        const msg = typeof obj.msg === "string" ? obj.msg
            : typeof obj.message === "string" ? obj.message
                : null;
        if (ts && msg)
            return `[${ts}] ${msg}`;
        if (msg)
            return msg;
        try {
            return JSON.stringify(entry);
        }
        catch {
            return String(entry);
        }
    }
    return String(entry);
}
/** Build the full clipboard payload (header + body). When `entries`
 *  is empty we still produce a useful message rather than an empty
 *  string so paste-into-chat is never silent. */
export function buildXRLogClipboardText(entries, env) {
    const header = `=== XR debug log — knaad lab — ${env.timestamp} ===\n` +
        `User-agent: ${env.userAgent}\n` +
        `Page URL: ${env.pageUrl}\n\n`;
    if (entries.length === 0)
        return header + EMPTY_MESSAGE + "\n";
    const body = entries.map(formatXRLogEntry).join("\n");
    return header + body + "\n";
}
/** Best-effort parse of the localStorage payload. Always returns an
 *  array; corrupt JSON or non-array data degrades to []. */
export function readStoredXRLog(read = () => {
    try {
        return localStorage.getItem(XR_DEBUG_LOG_KEY);
    }
    catch {
        return null;
    }
}) {
    const raw = read();
    if (!raw)
        return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed))
            return parsed;
        return [];
    }
    catch {
        // Fall back: treat the blob as a newline-delimited log.
        return raw.split("\n").filter((l) => l.length > 0);
    }
}
export function clearStoredXRLog() {
    try {
        localStorage.removeItem(XR_DEBUG_LOG_KEY);
    }
    catch { /* storage unavailable */ }
}
/** Snapshot of the runtime environment for the header. */
export function captureXRLogEnvironment() {
    return {
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== "undefined" && navigator.userAgent
            ? navigator.userAgent : "(unknown)",
        pageUrl: typeof window !== "undefined" && window.location
            ? window.location.href : "(unknown)"
    };
}
